import React, { FC, useEffect } from "react";
import { HiOutlineStar, HiStar, HiXMark } from "react-icons/hi2";
import {
  LuChevronLeft,
  LuChevronRight,
  LuInbox,
  LuPaperclip,
  LuSearch,
} from "react-icons/lu";

import { Email } from "../../../factories/emails/models/emails.models";
import { getLinkedFirm } from "../../../sessionStorage/sessionStorage";
import { AiEmailTriageItem } from "../../../slices/AiSlice";
import { useGetCustomersByEmailQuery } from "../../../slices/ContactsSlice";
import { initialsOf } from "../compose/composeEmail.utils";
import { SparkleBadge } from "../compose/SparkleIcon";
import {
  BriefLine,
  displayName,
  INTENTS,
  listDate,
  previewOf,
  ReadingPane,
  recipientOf,
  senderOf,
} from "./mailPage.utils";

export type MailFilter = "all" | "unread" | "clients";

const AVATAR_COLORS = [
  "#1B3E9F",
  "#6B4EFF",
  "#EA6D24",
  "#0E7C66",
  "#C2298A",
  "#8A93A6",
];
export const avatarColor = (value: string) => {
  let n = 0;
  for (let i = 0; i < value.length; i++) n = (n + value.charCodeAt(i)) % 997;
  return AVATAR_COLORS[n % AVATAR_COLORS.length];
};

/** Looks the address up among the firm's clients (cached per address). */
const ClientChip: FC<{
  address: string;
  onFound: (address: string, name: string) => void;
}> = ({ address, onFound }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data } = useGetCustomersByEmailQuery(
    { email: address, orgId },
    { skip: !address || !orgId },
  );
  const client = data?.[0];
  useEffect(() => {
    if (client) onFound(address.toLowerCase(), client.name || "");
  }, [client?.id]);
  if (!client) return null;
  return (
    <span
      className={
        "inline-flex h-[22px] min-w-0 max-w-[140px] items-center gap-[5px] rounded-[6px] bg-[#F3F4F7] px-2 text-[11.5px] font-bold text-[#3A4256]"
      }>
      <span
        className={"h-[7px] w-[7px] flex-none rounded-full"}
        style={{ background: client.businessContactId ? "#6B4EFF" : "#1B3E9F" }}
      />
      <span className={"truncate"}>{client.name}</span>
    </span>
  );
};

/**
 * Looks an address up among the firm's clients without showing anything, so
 * the Clients filter also knows the people of rows that aren't drawn, e.g.
 * every recipient of a Sent email (QA batch 19, MAIL-009).
 */
export const ClientLookup: FC<{
  address: string;
  onFound: (address: string, name: string) => void;
}> = ({ address, onFound }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const { data } = useGetCustomersByEmailQuery(
    { email: address, orgId },
    { skip: !address || !orgId },
  );
  const client = data?.[0];
  useEffect(() => {
    if (client) onFound(address.toLowerCase(), client.name || "");
  }, [client?.id]);
  return null;
};

type Props = {
  emails: Email[];
  openId: string | null;
  onOpen: (email: Email) => void;
  onStar: (email: Email) => void;
  outgoing: boolean;
  tags: Record<string, AiEmailTriageItem>;
  search: string;
  setSearch: (value: string) => void;
  searchPlaceholder: string;
  filter: MailFilter;
  setFilter: (value: MailFilter) => void;
  brief: BriefLine[];
  briefExtra: string;
  showBrief: boolean;
  onHideBrief: () => void;
  /** Reading pane: "right" = narrow column, "bottom" = top strip, "off" = full width. */
  pane: ReadingPane;
  onClientFound: (address: string, name: string) => void;
  loading: boolean;
  emptyTitle: string;
  emptyText: string;
  page: number;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
};

const PANE_CLASS: Record<ReadingPane, string> = {
  right: "w-[384px] flex-none",
  bottom: "h-[46%] min-h-[260px] w-full flex-none",
  off: "min-w-0 flex-1",
};

/** Email list: search, filters, AI inbox brief and the email rows. */
export const MailList: FC<Props> = (p) => (
  <section
    aria-label={"Emails"}
    className={`flex min-h-0 flex-col overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white ${
      PANE_CLASS[p.pane]
    }`}>
    <div
      className={
        "flex flex-none flex-col gap-[10px] border-b border-[#F0F1F4] px-[14px] pb-[10px] pt-[14px]"
      }>
      <div className={"relative"}>
        <LuSearch
          size={16}
          className={"absolute left-3 top-1/2 -translate-y-1/2 text-[#9097A8]"}
        />
        <input
          value={p.search}
          onChange={(e) => p.setSearch(e.target.value)}
          placeholder={p.searchPlaceholder}
          className={
            "h-[38px] w-full rounded-[8px] border-[1.5px] border-[#DCE2EA] bg-white pl-9 pr-[34px] text-[14px] text-[#1F2433] outline-none focus:border-[#6B4EFF] focus:shadow-[0_0_0_3px_rgba(107,78,255,0.16)]"
          }
        />
        {p.search && (
          <button
            type={"button"}
            aria-label={"Clear search"}
            onClick={() => p.setSearch("")}
            className={
              "absolute right-[10px] top-1/2 inline-flex -translate-y-1/2 text-[#9097A8]"
            }>
            <HiXMark size={15} />
          </button>
        )}
      </div>
      <div className={"flex items-center gap-[6px]"}>
        {(
          [
            ["all", "All"],
            ["unread", "Unread"],
            ["clients", "Clients"],
          ] as [MailFilter, string][]
        ).map(([key, label]) => (
          <button
            type={"button"}
            key={key}
            onClick={() => p.setFilter(key)}
            className={
              "inline-flex h-7 items-center rounded-[14px] border px-3 text-[12.5px] font-bold"
            }
            style={
              p.filter === key
                ? {
                    background: "#1F2433",
                    borderColor: "#1F2433",
                    color: "#fff",
                  }
                : {
                    background: "#fff",
                    borderColor: "#DCE2EA",
                    color: "#3A4256",
                  }
            }>
            {label}
          </button>
        ))}
      </div>
    </div>

    <div className={"min-h-0 flex-1 overflow-y-auto"}>
      {p.showBrief && p.brief.length > 0 && (
        <div
          className={
            "mx-3 mb-[6px] mt-3 rounded-[12px] border border-[#E4DCFF] px-[14px] pb-3 pt-[13px]"
          }
          style={{
            background: "linear-gradient(135deg,#F5F1FF 0%,#FBF8FF 100%)",
          }}>
          <div className={"flex items-center gap-2"}>
            <SparkleBadge size={24} />
            <span
              className={"flex-1 text-[13.5px] font-extrabold text-[#2A1F66]"}>
              {p.brief.length === 1
                ? "1 email needs you"
                : `${p.brief.length} emails need you`}
            </span>
            <button
              type={"button"}
              aria-label={"Hide brief"}
              onClick={p.onHideBrief}
              className={
                "inline-flex rounded-[5px] p-[2px] text-[#A79BE0] hover:bg-[#EEE9FF] hover:text-[#6B4EFF]"
              }>
              <HiXMark size={14} />
            </button>
          </div>
          <div className={"mt-2 flex flex-col gap-[2px]"}>
            {p.brief.map((b) => (
              <button
                type={"button"}
                key={b.id}
                onClick={() => {
                  const email = p.emails.find((e) => e.id === b.id);
                  if (email) p.onOpen(email);
                }}
                className={
                  "-mx-[6px] flex items-start gap-2 rounded-[7px] px-[6px] py-[5px] text-left text-[13px] leading-[1.45] text-[#3A4256] hover:bg-[#EEE9FF]"
                }>
                <span
                  className={"mt-[7px] h-[6px] w-[6px] flex-none rounded-full"}
                  style={{ background: b.dot }}
                />
                <span>
                  <strong className={"font-bold text-[#1F2433]"}>
                    {b.who}
                  </strong>{" "}
                  {b.what}
                </span>
              </button>
            ))}
          </div>
          {p.briefExtra && (
            <div className={"mt-2 text-[12px] text-[#9A90C8]"}>
              {p.briefExtra}
            </div>
          )}
        </div>
      )}

      {p.loading && (
        <div className={"flex flex-col gap-3 p-4"}>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={"flex animate-pulse gap-3"}>
              <span className={"h-9 w-9 rounded-full bg-[#EEF0F4]"} />
              <span className={"flex flex-1 flex-col gap-2"}>
                <span className={"h-3 w-1/2 rounded bg-[#EEF0F4]"} />
                <span className={"h-3 w-4/5 rounded bg-[#F3F4F7]"} />
                <span className={"h-3 w-3/5 rounded bg-[#F3F4F7]"} />
              </span>
            </div>
          ))}
        </div>
      )}

      {!p.loading &&
        p.emails.map((e) => {
          const person = p.outgoing ? recipientOf(e) : senderOf(e);
          const name = displayName(person);
          const tag = p.tags[e.id] ? INTENTS[p.tags[e.id].intent] : null;
          const on = p.openId === e.id;
          const hasFiles = !!e.attachments?.some((a) => !a.is_inline);
          return (
            <div
              key={e.id}
              role={"button"}
              tabIndex={0}
              onClick={() => p.onOpen(e)}
              onKeyDown={(ev) => ev.key === "Enter" && p.onOpen(e)}
              className={
                "group flex cursor-pointer gap-3 border-b border-[#F1F2F5] px-4 py-[13px] hover:bg-[#F7F6FF]"
              }
              style={{
                background: on ? "#F4F1FF" : undefined,
                boxShadow: on ? "inset 3px 0 0 #6B4EFF" : "none",
              }}>
              <span
                className={
                  "inline-flex h-9 w-9 flex-none items-center justify-center rounded-full text-[12px] font-bold text-white"
                }
                style={{ background: avatarColor(person?.email || name) }}>
                {initialsOf(name)}
              </span>
              <div className={"flex min-w-0 flex-1 flex-col gap-[2px]"}>
                <div className={"flex items-center gap-2"}>
                  {e.unread && (
                    <span
                      className={"h-2 w-2 flex-none rounded-full bg-[#6B4EFF]"}
                    />
                  )}
                  <span
                    className={
                      "min-w-0 flex-1 truncate text-[14px] text-[#1F2433]"
                    }
                    style={{ fontWeight: e.unread ? 800 : 600 }}>
                    {p.outgoing ? `To: ${name}` : name}
                  </span>
                  {hasFiles && (
                    <LuPaperclip
                      size={13}
                      className={"flex-none text-[#A3A9B8]"}
                      aria-label={"Has attachments"}
                    />
                  )}
                  <span
                    className={"flex-none text-[12px]"}
                    style={{
                      color: e.unread ? "#6B4EFF" : "#9097A8",
                      fontWeight: e.unread ? 700 : 500,
                    }}>
                    {listDate(e.date)}
                  </span>
                </div>
                <div className={"flex min-w-0 items-center gap-[6px]"}>
                  <span
                    className={
                      "min-w-0 flex-1 truncate text-[13.5px] text-[#1F2433]"
                    }
                    style={{ fontWeight: e.unread ? 700 : 500 }}>
                    {e.subject || "(no subject)"}
                  </span>
                  <button
                    type={"button"}
                    aria-label={e.starred ? "Unstar" : "Star"}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      p.onStar(e);
                    }}
                    className={
                      e.starred
                        ? "inline-flex text-[#F5B400]"
                        : "inline-flex text-[#C2C7D2] opacity-0 group-hover:opacity-100"
                    }>
                    {e.starred ? (
                      <HiStar size={14} />
                    ) : (
                      <HiOutlineStar size={14} />
                    )}
                  </button>
                </div>
                <div
                  className={
                    "line-clamp-2 text-[13px] leading-[1.45] text-[#7D8396]"
                  }>
                  {previewOf(e)}
                </div>
                <div
                  className={
                    "mt-[5px] flex min-h-[22px] items-center gap-[5px] overflow-hidden"
                  }>
                  {tag && (
                    <span
                      className={
                        "inline-flex h-[22px] flex-none items-center rounded-[6px] px-2 text-[11.5px] font-bold"
                      }
                      style={{ background: tag.bg, color: tag.color }}>
                      {tag.label}
                    </span>
                  )}
                  {person?.email && (
                    <ClientChip
                      address={person.email}
                      onFound={p.onClientFound}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}

      {!p.loading && p.emails.length === 0 && (
        <div
          className={
            "flex min-h-[260px] flex-col items-center justify-center gap-[10px] p-6 text-center"
          }>
          <span
            className={
              "inline-flex h-12 w-12 items-center justify-center rounded-[12px] bg-[#F3F4F7] text-[#9097A8]"
            }>
            <LuInbox size={22} />
          </span>
          <span className={"text-[14px] font-bold text-[#3A4256]"}>
            {p.emptyTitle}
          </span>
          <span className={"text-[13px] text-[#9097A8]"}>{p.emptyText}</span>
        </div>
      )}
    </div>

    <div
      className={
        "flex flex-none items-center justify-between border-t border-[#F0F1F4] px-4 py-[10px] text-[12.5px] text-[#8A90A2]"
      }>
      <span>
        {p.emails.length
          ? `Page ${p.page} · ${p.emails.length} emails`
          : "0 emails"}
      </span>
      <span className={"flex gap-1"}>
        <button
          type={"button"}
          aria-label={"Previous page"}
          disabled={p.page <= 1}
          onClick={p.onPrev}
          className={
            "inline-flex h-7 w-7 items-center justify-center rounded-[7px] text-[#5B6678] hover:bg-[#F4F2FF] disabled:text-[#C2C7D2] disabled:hover:bg-transparent"
          }>
          <LuChevronLeft size={15} />
        </button>
        <button
          type={"button"}
          aria-label={"Next page"}
          disabled={!p.hasNext}
          onClick={p.onNext}
          className={
            "inline-flex h-7 w-7 items-center justify-center rounded-[7px] text-[#5B6678] hover:bg-[#F4F2FF] disabled:text-[#C2C7D2] disabled:hover:bg-transparent"
          }>
          <LuChevronRight size={15} />
        </button>
      </span>
    </div>
  </section>
);
